import { useToast } from '@/composables/useToast';
import { Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  IMassUploadFile,
  IRenditionFile,
  IUploadRecord,
} from '../MassUpload.types';
import { QuickXorHash } from '@/utils/xorHasher';
import axios from 'axios';
import { endpointsList } from '@/helpers';
import { prodisPlatformUrls } from '@/static/platform-urls';
import { IVueexState } from '@/store';
import { useStore } from 'vuex';
import { ProdisDuplicateBulkResponse } from 'types';

type FileMatcher = (
  uploadRecord: IUploadRecord | IRenditionFile,
  file: IMassUploadFile,
) => boolean;

export function useFilesMapping(
  massUploadFiles: Ref<IMassUploadFile[]>,
  uploadRecords: Ref<IUploadRecord[]>,
  isReadingZipFile: Ref<boolean>,
  projectId: string,
) {
  const { createWarningToast } = useToast();
  const { t } = useI18n();
  const store = useStore<IVueexState>();

  watch(uploadRecords, async () => {
    if (massUploadFiles.value.length) {
      await mapFilesToFilePaths(true);
    } else await mapFilesToFilePaths(false);
  });

  watch(
    massUploadFiles,
    async () => {
      if (uploadRecords.value.length) {
        await mapFilesToFilePaths(true);
      } else await mapFilesToFilePaths(false);
    },
    { deep: true },
  );

  const fileMatchers: FileMatcher[] = [
    matchFilepath,
    matchFilepathWithoutDirectory,
    matchFileName,
  ];

  let isMappingInProgress = false;

  async function mapFilesToFilePaths(warnAboutNoFilesMatched = false) {
    if (isMappingInProgress) return;
    isMappingInProgress = true;
    try {
      let isAtLeastOneFileFound = false;
      const hashMap: Record<string, string> = {}; // key: documentId, value: hash

      // Phase 1: synchronous file matching — assign files to records before any
      // await to avoid triggering the deep uploadRecords watcher mid-async.
      const recordsToHash: { record: IUploadRecord; file: IMassUploadFile }[] =
        [];
      const renditionRecordsToHash: {
        record: IUploadRecord;
        file: IMassUploadFile;
      }[] = [];
      massUploadFiles.value.forEach((file) => {
        const recordWithMatchingMainFile = uploadRecords.value.find(
          (record) => {
            /**
             * The record can already have a file if **isNoMetadataUploadMode**
             * is on.
             */
            if (record.file) {
              isAtLeastOneFileFound = true;
              return;
            }

            for (const matcher of fileMatchers) {
              if (matcher(record, file)) {
                isAtLeastOneFileFound = true;
                return true;
              }
            }
          },
        );
        if (recordWithMatchingMainFile) {
          recordWithMatchingMainFile.file = file.file;
          recordsToHash.push({ record: recordWithMatchingMainFile, file });
        }
        const recordWithMatchingRenditionFile = uploadRecords.value.find(
          (record) => {
            if (!record.renditionFile) return;
            if (record.renditionFile.file) {
              isAtLeastOneFileFound = true;
              return;
            }

            for (const matcher of fileMatchers) {
              if (matcher(record.renditionFile, file)) {
                isAtLeastOneFileFound = true;
                return true;
              }
            }
          },
        );
        if (recordWithMatchingRenditionFile) {
          recordWithMatchingRenditionFile.renditionFile!.file = file.file;
          renditionRecordsToHash.push({
            record: recordWithMatchingRenditionFile,
            file,
          });
        }
      });

      // Phase 2: async hashing — all file assignments are done, watcher re-entry
      // is now harmless since every record.file is already set.
      for (let i = 0; i < recordsToHash.length; i++) {
        const { record, file } = recordsToHash[i];
        const fileHash = await QuickXorHash.fromFile(file.file);
        if (record.metadata) {
          hashMap[record.metadata.documentId] = fileHash;
        }
      }

      const renditionHashMap: Record<string, string> = {}; // key: documentId, value: renditionHash
      for (let i = 0; i < renditionRecordsToHash.length; i++) {
        const { record, file } = renditionRecordsToHash[i];
        const fileHash = await QuickXorHash.fromFile(file.file);
        if (record.metadata) {
          renditionHashMap[record.metadata.documentId] = fileHash;
        }
      }

      if (warnAboutNoFilesMatched && !isAtLeastOneFileFound) {
        createWarningToast(t('documentUpload.warnings.noFilesFound'));
      }

      if (Object.keys(hashMap).length || Object.keys(renditionHashMap).length) {
        const docIds = Object.keys(hashMap);
        if (docIds.length) {
          const bulkPayload: {
            projectId: string;
            documents: {
              id: string;
              xorHash: string;
            }[];
          } = {
            projectId,
            documents: docIds.map((docId) => ({
              id: docId,
              xorHash: hashMap[docId],
            })),
          };

          const duplicates = (
            await axios.post<ProdisDuplicateBulkResponse>(
              endpointsList.uploadHandler.getDublicatesBulk(),
              bulkPayload,
            )
          ).data;
          uploadRecords.value.forEach((record) => {
            if (record && record.metadata && record.metadata.documentId) {
              if (
                duplicates[record.metadata.documentId] &&
                duplicates[record.metadata.documentId].fileDuplicates &&
                duplicates[record.metadata.documentId].fileDuplicates.length
              ) {
                record.duplicate = true;
                record.daisyLinks = duplicates[
                  record.metadata.documentId
                ].fileDuplicates.map(
                  (doc) =>
                    `${prodisPlatformUrls[store.state.env!]}/documents/detail/${doc._id}`,
                );
              }
            }
          });
        }
        const renditionDocIds = Object.keys(renditionHashMap);
        if (renditionDocIds.length) {
          const renditionBulkPayload: {
            projectId: string;
            documents: {
              id: string;
              xorHash: string;
            }[];
          } = {
            projectId,
            documents: renditionDocIds.map((docId) => ({
              id: docId,
              xorHash: renditionHashMap[docId],
            })),
          };

          const renditionDuplicates = (
            await axios.post<ProdisDuplicateBulkResponse>(
              endpointsList.uploadHandler.getDublicatesBulk(),
              renditionBulkPayload,
            )
          ).data;

          uploadRecords.value.forEach((record) => {
            if (record?.renditionFile && record.metadata?.documentId) {
              if (
                renditionDuplicates[record.metadata.documentId] &&
                renditionDuplicates[record.metadata.documentId]
                  .fileDuplicates &&
                renditionDuplicates[record.metadata.documentId].fileDuplicates
                  .length
              ) {
                record.renditionFile.duplicate = true;
                record.renditionFile.daisyLinks = renditionDuplicates[
                  record.metadata.documentId
                ].fileDuplicates.map(
                  (doc) =>
                    `${prodisPlatformUrls[store.state.env!]}/documents/detail/${doc._id}`,
                );
              }
            }
          });
        }
      }
    } finally {
      isMappingInProgress = false;
      isReadingZipFile.value = false;
    }
  }

  /**
   * Looking for a file in the ZIP matching the provided file path
   * with directory name (ZIP name) included
   * @example
   * ```
   * ZIP path: zip-directory-name/some-file.pdf
   * file path from CSV: zip-directory-name/some-file.pdf
   * ```
   */
  function matchFilepath(
    uploadRecord: IUploadRecord | IRenditionFile,
    file: IMassUploadFile,
  ): boolean {
    return (
      uploadRecord.metadata?.Filepath?.toLowerCase().trim().normalize('NFC') ===
      file.path.toLowerCase().trim().normalize('NFC')
    );
  }

  /**
   * Looking for a file in the ZIP ignoring the directory name
   * (ZIP name)
   * @example
   * ```
   * ZIP path: zip-directory-name/some-file.pdf
   * file path from CSV: some-file.pdf
   * ```
   */
  function matchFilepathWithoutDirectory(
    uploadRecord: IUploadRecord | IRenditionFile,
    file: IMassUploadFile,
  ): boolean {
    const pathWithoutDirectory = getPathWithoutDirectory(file);
    return (
      uploadRecord?.metadata?.Filepath?.toLowerCase()
        .trim()
        .normalize('NFC') ===
      pathWithoutDirectory.toLowerCase().trim().normalize('NFC')
    );
  }

  /**
   * If path was not supplied or does not match any file, we are
   * looking for a file lying directly in the ZIP file with
   * a matching name
   * @example
   * ```
   * ZIP path: zip-directory-name/some-file.pdf
   * file name from CSV: some-file.pdf
   * ```
   */
  function matchFileName(
    uploadRecord: IUploadRecord | IRenditionFile,
    file: IMassUploadFile,
  ): boolean {
    const pathWithoutDirectory = getPathWithoutDirectory(file);
    return (
      uploadRecord?.metadata?.Name?.toLowerCase().trim().normalize('NFC') ===
      pathWithoutDirectory.toLowerCase().trim().normalize('NFC')
    );
  }

  function getPathWithoutDirectory(file: IMassUploadFile): string {
    const pathArray = file.path.split('/');
    pathArray.shift();
    return pathArray.join('/');
  }
}
